import { get, put } from './secure_fetch.js';

const CACHE_KEY = 'user_prefs';
const THROTTLE_MS = 100;
const DEFAULT_SAMPLE_FONT_SIZE = 36;

// Debounce state for updatePrefs
let updatePrefsTimeout = null;

export const clearCache = () => {
  localStorage.removeItem(CACHE_KEY);
};

const getCachedPrefs = () => {
  try {
    const prefs = localStorage.getItem(CACHE_KEY);
    return prefs ? JSON.parse(prefs) : null;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return null;
  }
};

const setCachedPrefs = (prefs) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(prefs));
  } catch (error) {
    console.error('Error writing to localStorage:', error);
  }
};

export const getUserPrefs = () => {
  return new Promise((resolve, reject) => {
    const cachedPrefs = getCachedPrefs();
    if (cachedPrefs) {
      resolve(cachedPrefs);
    } else {
      try {
        get('/account/prefs.json').then((response) => response.json()).then((data) => {
          const prefs = _getFormattedPrefData(data || {});
          setCachedPrefs(prefs);
          resolve(prefs);
        });
      } catch (error) {
        console.error('Error loading user preferences:', error);
        reject(error);
      }
    }
  });
};

  /**
   * Updates the preferences for a user
   *
   * @param {{
  *   allFiltersExpanded?: Boolean,
  *   filterSidebarVisibility?: String, // open-filters-sidebar or close-filters-sidebar
  *   exampleText?: {
  *     value: String,
  *     size: Number
  *   },
  *   isFilterListHiddenByUser?: Boolean,
  *   selectedViewType?: String,
  *   hideTypekitOnboardModal?: Boolean,
  *   hideFontActivationModal?: Boolean,
  *   hideUseModelFontActivationModal?: Boolean,
  *   hideUseModelCCDTutorial?: Boolean,
  *   hideLibrariesCoachmark?: Boolean,
  *   hideRemoveFamilyConfirmationModal?: Boolean,
  *   hideFamilyRetirementNotification?: String, // string of comma separated slugs
  *   hideFoundryRetirementNotification?: String, // string of comma separated slugs
  *   hideImages?: Boolean,
  *   hideRemoveFromFavoritesDialog?: Boolean,
  *   hideRemoveFromLibraryDialog?: Boolean,
  *   showBasicLibrary?: Boolean,
  *   showPaidFonts?: Boolean,
  * }} prefsToUpdate
  *
  * @returns {Promise}
  */
export const updatePrefs = (prefsToUpdate) => {
  // Update local cache immediately for responsive UI
  getUserPrefs().then((currentPrefs) => {
    setCachedPrefs({ ...currentPrefs, ...prefsToUpdate });
  });

  // Debounce the HTTP request to avoid server spam
  return new Promise((resolve, reject) => {
    // Clear any existing timeout
    if (updatePrefsTimeout) {
      clearTimeout(updatePrefsTimeout);
    }

    updatePrefsTimeout = setTimeout(async () => {
      try {
        const response = await put('/account/prefs.json', { body: JSON.stringify(_getHttpParams(prefsToUpdate)) });

        if (response.ok && prefsToUpdate.exampleText?.value) {
          window._afSampleTextChanged = true;
        }

        resolve();
      } catch (error) {
        console.error('Error updating user prefs:', error);
        reject(error);
      }
    }, THROTTLE_MS);
  });
};

/**
 * Take the raw params a method call and format them for an HTTP request.
 *
 * @private
 * @param params {Object}
 * @returns {Object}
 */
function _getHttpParams(params) {
  const formatted = {};

  if (params.exampleText) {
    formatted.browse_families_sample_text = params.exampleText.value;
    formatted.browse_families_sample_size = params.exampleText.size;
  }

  if (params.selectedViewType) {
    formatted.browse_families_view_type = params.selectedViewType;
  }

  if (params.isFilterListHiddenByUser) {
    formatted.browse_filter_drawer_state = 'close';
  } else {
    formatted.browse_filter_drawer_state = 'open';
  }

  if (params.browseMode) {
    formatted.browse_families_browse_mode = params.browseMode;
    // When toggling browseMode, reset the sample text details
    // so that default Japanese and english strings appear accordingly.
    if (!params.exampleText) {
      formatted.browse_families_sample_text = '';
      formatted.browse_families_sample_size = 'null';
    }
  }

  if (params.hideTypekitOnboardModal) {
    formatted.hide_typekit_onboard_modal = true;
  }

  if (params.hideFontActivationModal) {
    formatted.hide_font_activation_modal = true;
  }

  if (params.hideUseModelFontActivationModal) {
    formatted.hide_use_model_font_activation_modal = true;
  }

  if (params.hideUseModelCCDTutorial) {
    formatted.hide_use_model_ccd_tutorial = true;
  }

  if (params.hideLibrariesCoachmark) {
    formatted.hide_libraries_coachmark = params.hideLibrariesCoachmark;
  }

  if (params.hideRemoveFamilyConfirmationModal) {
    formatted.hide_remove_family_confirmation_modal = true;
  }

  if (params.hideFamilyRetirementNotification) {
    formatted.hide_family_retirement_notification = params.hideFamilyRetirementNotification;
  }

  if (params.hideFoundryRetirementNotification) {
    formatted.hide_foundry_retirement_notification = params.hideFoundryRetirementNotification;
  }

  if (params.allFiltersExpanded) {
    formatted.all_filters_expanded = params.allFiltersExpanded;
  }

  if (params.filterSidebarVisibility) {
    formatted.filter_sidebar_visibility = params.filterSidebarVisibility;
  }

  formatted.hide_images = params.hideImages;
  formatted.hide_remove_from_favorites_dialog = params.hideRemoveFromFavoritesDialog;
  formatted.hide_remove_from_library_dialog = params.hideRemoveFromLibraryDialog;
  formatted.show_basic_library = params.showBasicLibrary;
  formatted.show_paid_fonts = params.showPaidFonts;

  return {prefs: formatted};
}

/**
  * Get a formatted version of the user's stored preferences.
  *
  * @private
  * @param {{
*   browse_families_sample_text: {String},
*   browse_families_sample_size: {String},
*   browse_families_view_type: {String},
*   browse_families_browse_mode: {String},
*   browse_filter_drawer_state: {String},
*   hide_typekit_onboard_modal: {Boolean},
*   all_filters_expanded: {Boolean},
*   filter_sidebar_visibility: {String}
* }} userPrefs
*
* @returns {{exampleText: {value: (any), size: (any)}, isFilterListHiddenByUser: boolean, selectedViewType: (any)}|{}}
*/
function _getFormattedPrefData(userPrefs) {
  const state = {};
  const allowedKeys = [
    'browse_families_sample_text',
    'browse_families_sample_size',
    'browse_families_view_type',
    'browse_families_browse_mode',
    'browse_filter_drawer_state',
    'hide_typekit_onboard_modal',
    'hide_font_activation_modal',
    'hide_libraries_coachmark',
    'hide_use_model_ccd_tutorial',
    'hide_use_model_font_activation_modal',
    'hide_remove_family_confirmation_modal',
    'hide_images',
    'show_paid_fonts',
    'show_basic_library',
    'all_filters_expanded',
    'filter_sidebar_visibility'
  ];
  const _userPrefs = Object.fromEntries(
    Object.entries(userPrefs).filter(([key]) => allowedKeys.includes(key))
  );

  state.exampleText = {
    value: _userPrefs.browse_families_sample_text,
    size: _userPrefs.browse_families_sample_size || DEFAULT_SAMPLE_FONT_SIZE
  };

  if (_userPrefs.browse_filter_drawer_state) {
    state.isFilterListHiddenByUser = _userPrefs.browse_filter_drawer_state === 'close';
  }

  if (_userPrefs.browse_families_view_type) {
    state.selectedViewType = _userPrefs.browse_families_view_type;
  }

  if (_userPrefs.browse_families_browse_mode) {
    state.browseMode = _userPrefs.browse_families_browse_mode;
  }

  if (_userPrefs.hide_typekit_onboard_modal) {
    state.hideTypekitOnboardModal = _userPrefs.hide_typekit_onboard_modal === 'true';
  }

  if (_userPrefs.hide_font_activation_modal) {
    state.hideFontActivationModal = _userPrefs.hide_font_activation_modal === 'true';
  }

  if (_userPrefs.hide_use_model_font_activation_modal) {
    state.hideUseModelFontActivationModal = _userPrefs.hide_use_model_font_activation_modal === 'true';
  }

  if (_userPrefs.hide_use_model_ccd_tutorial) {
    state.hideUseModelCCDTutorial = _userPrefs.hide_use_model_ccd_tutorial === 'true';
  }

  if (_userPrefs.hide_libraries_coachmark) {
    state.hideLibrariesCoachmark = _userPrefs.hide_libraries_coachmark;
  }

  if (_userPrefs.hide_remove_family_confirmation_modal) {
    state.hideRemoveFamilyConfirmationModal = _userPrefs.hide_remove_family_confirmation_modal === 'true';
  }

  if (_userPrefs.all_filters_expanded) {
    state.allFiltersExpanded = _userPrefs.all_filters_expanded === 'true' || _userPrefs.all_filters_expanded === true;
  }

  if (_userPrefs.filter_sidebar_visibility) {
    state.filterSidebarVisibility = _userPrefs.filter_sidebar_visibility;
  }

  if (Object.prototype.hasOwnProperty.call(_userPrefs, 'hide_images')) {
    state.hideImages = _userPrefs.hide_images === 'true' || _userPrefs.hide_images === true;
  }

  if (Object.prototype.hasOwnProperty.call(_userPrefs, 'show_paid_fonts')) {
    state.showPaidFonts = _userPrefs.show_paid_fonts === 'true' || _userPrefs.show_paid_fonts === true;
  }

  if (Object.prototype.hasOwnProperty.call(_userPrefs, 'show_basic_library')) {
    state.showBasicLibrary = _userPrefs.show_basic_library === 'true' || _userPrefs.show_basic_library === true;
  }

  return state;
}
